import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';

import ScoreBar from '../PerformanceReview4c/ScoreBar';
import Proctorship from '../PerformanceReview4c/Proctorship';
import InfoIconWithTooltip from 'components/InfoIconWithTooltip';

import { IdealCandidateThreshold, ImprovementFactor, NextScoreStep } from './constants';

// Current score reads as "you", matching the radar's Your Performance navy; the
// next achievable score is blue and the ideal-candidate goal green.
const CURRENT_MARKER_COLOR = '#002547';
const NEXT_MARKER_COLOR = '#135EBF';

// A marker label is a value over a caption. The value line always carries the
// bar's own near-black; the caption takes neutral grey, except where it names
// the ideal-candidate goal - or a current score that has reached it - and turns
// the same green as that benchmark.
const CAPTION_COLOR = '#636F7F';

// Two decimals, as in the score in the card header, with a single trailing zero
// dropped so 9.30 reads as 9.3 and 5.50 as 5.5 while 4.38 keeps both places.
const formatScore = (score) => score.toFixed(2).replace(/0$/, '');

// Current score x 1.20, rounded up to the nearest half point, capped at the
// ideal-candidate threshold. The epsilon stops binary float error on an exact
// multiple (7.5 * 1.2 = 8.999...) from pushing the result up a whole step.
export const toNextAchievable = (score) => {
  const stepped = Math.ceil((score * ImprovementFactor) / NextScoreStep - 1e-9) * NextScoreStep;

  return Math.min(Math.round(stepped * 10) / 10, IdealCandidateThreshold);
};

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(4.5),
    width: '100%'
  },
  scoreComponent: {
    display: 'flex',
    justifyContent: "space-between",
    alignItems: 'flex-start',
    width: '100%'
  },
  scoreSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  scoreHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginBottom: theme.spacing(2)
  }
}));

const getFeedbackByScore = (score, maxScore = 10) => {
  switch (true) {
    case (score >= 8):
      return "Excellent performance! Your case interview skills demonstrate strong analytical thinking and clear communication.";
    case (score >= 6):
      return "Good work! Continue refining your framework development and data analysis skills to reach the next level.";
    case (score >= 4):
      return "You're making progress! Focus on structuring your approach and improving hypothesis testing.";
    case (score >= 2):
      return "Keep practicing! Work on case introduction and developing logical frameworks.";
    default:
      return "Let's start with the fundamentals — focus on understanding case interview basics first";
  }
};

/**
 * The three display cases from the score-scale spec, kept in one exported place
 * so the preview harness renders exactly what the page renders.
 *
 *   next < 9     three markers: current, next achievable, goal
 *   next >= 9    current, plus one merged marker on the goal
 *   current >= 9 current at its value and the goal at 9; if they coincide the
 *                goal is the only head drawn
 */
export const buildScoreMarkers = (currentScore, nextScore, idealColor) => {
  // Past the threshold the current score is no longer chasing anything, so it
  // drops the navy for the goal's green and its head takes a filled centre.
  const cleared = currentScore >= IdealCandidateThreshold;

  const current = {
    value: currentScore,
    valueLabel: formatScore(currentScore),
    captions: [{ text: 'Your score', color: cleared ? idealColor : CAPTION_COLOR }],
    color: cleared ? idealColor : CURRENT_MARKER_COLOR,
    variant: cleared ? 'cleared' : 'current'
  };

  const idealCaption = { text: 'Ideal candidate score', color: idealColor };

  const ideal = {
    value: IdealCandidateThreshold,
    valueLabel: `${IdealCandidateThreshold}+`,
    captions: [idealCaption],
    color: idealColor,
    variant: 'ideal'
  };

  if (cleared) {
    if (currentScore === IdealCandidateThreshold) {
      return [
        { ...current, hideHead: true },
        { ...ideal, variant: 'merged', coreColor: CURRENT_MARKER_COLOR }
      ];
    }

    return [current, ideal];
  }

  if (nextScore >= IdealCandidateThreshold) {
    return [
      current,
      {
        ...ideal,
        variant: 'merged',
        coreColor: NEXT_MARKER_COLOR,
        pulse: true,
        // One head stands for both benchmarks, so both captions hang off the
        // single 9+ they agree on.
        captions: [{ text: 'Next achievable score', color: CAPTION_COLOR }, idealCaption]
      }
    ];
  }

  return [
    current,
    {
      value: nextScore,
      valueLabel: formatScore(nextScore),
      captions: [{ text: 'Next achievable score', color: CAPTION_COLOR }],
      color: NEXT_MARKER_COLOR,
      variant: 'next',
      pulse: true
    },
    ideal
  ];
};

const CaseMasterScore = ({ 
  score, 
  proctorRecords = [],
  maxScore = 10, 
  scoreLabel = "My score:", 
  tooltipTitle = "Score based on case interview performance across multiple dimensions.",
  showDescription = false
}) => {
  const classes = useStyles();
  const theme = useTheme();

  const getCatIndex = React.useCallback((score) => {
    // For case-master (1-10 scale), map to cat GIFs 1-5
    const clamped = Math.min(Math.max(score, 1), 10);
    // Map 1-10 scale to 1-5 scale for cat GIFs
    const mappedScore = Math.ceil((clamped / 10) * 5);
    return Math.min(Math.max(mappedScore, 1), 5);
  }, []);

  const currentScore = Math.min(Math.max(score, 1), maxScore);

  const nextScore = toNextAchievable(currentScore);

  const markers = React.useMemo(
    () => buildScoreMarkers(currentScore, nextScore, theme.palette.success['clr-500']),
    [currentScore, nextScore, theme]
  );

  return (
    <Box className={classes.container}>
      <Box className={classes.scoreComponent}>
        <Box className={classes.scoreSection}>
          <Box className={classes.scoreHeader}>
            <Typography variant='h4-medium'>{scoreLabel} </Typography>
            <Typography variant='h4-bold' color='primary.clr-400'>{currentScore.toFixed(2)}</Typography>
            <img
              src={`https://assets.languify.in/gifs/cat-${getCatIndex(score)}.gif`}
              alt='cat-gif'
              width={36}
              style={{ marginLeft: '8px' }}
            />
            <InfoIconWithTooltip
              title={tooltipTitle}
              sx={{ fontSize: '35px', marginTop: '2px', marginLeft: '16.5px' }} 
              placement='top'
            />
          </Box>
          <ScoreBar
            scale={currentScore}
            maxScale={maxScore}
            width='880px'
            barHeight='32px'
            markers={markers}
            gap={nextScore > currentScore ? { from: currentScore, to: nextScore } : null}
          />
        </Box>

        <Proctorship records={proctorRecords} />
      </Box>

      {showDescription && (
        <Box sx={{ padding: theme.spacing(2, 0) }}>
          <Typography
            variant='h6-medium'
            color='neutral.clr-600'
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {getFeedbackByScore(parseInt(Math.min(Math.max(score, 1), 10)), maxScore)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CaseMasterScore;
